import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import {
  CalendarIcon,
  CheckIcon,
  ChevronRightIcon,
  Pencil2Icon,
} from "@radix-ui/react-icons";
import clsx from "clsx";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useGetInstance } from "src/api/General/instance";
import { SettingsData, useGetSettings } from "src/api/General/settings";
import {
  CapacityData,
  CapacityTimelineData,
  useGetUserCapacities,
  UserAppsData,
  useUpdateInstanceCapacity,
  useUpdateUserCapacity,
} from "src/api/timesheets/timesheets";
import { Button } from "src/components/ui/button";
import { Calendar } from "src/components/ui/calendar";
import { Checkbox } from "src/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import InputHeading from "src/components/ui/Headings/InputHeading";
import { Loading } from "src/components/ui/loading";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { convertMsToNatural } from "src/pages/timesheets/timesheets";

type Props = {};

export default function CapacitySettings({}: Props) {
  const { instance } = useGetInstance();
  const { data: settings } = useGetSettings();
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 items-center border-b border-gray-200/70 px-8 text-sm">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/settings`}>
            <div className="text-gray-800">{instance?.name}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Capacity"}</div>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <div className="mx-auto mt-20 max-w-3xl px-8">
          <div className="border-b pb-8">
            <h2 className="cursor-default text-lg font-medium tracking-wide text-gray-900">
              Capacity
            </h2>
          </div>
          {/* <div className="mt-6 flex items-center justify-between">
            <div>
            </div>
          </div> */}
          {/* <IntegrationStatus /> */}
          <div>{settings && <Capacity settings={settings} />}</div>
        </div>
      </div>
    </div>
  );
}

function Capacity({ settings }: { settings: SettingsData }) {
  const updateInstanceCapacity = useUpdateInstanceCapacity();
  const workspaceCapacityToDisplay = useMemo(() => {
    return getCurrentActiveCapacity(
      settings?.timeTracking.defaultCapacityTimeline
    );
  }, [settings]);

  const [workspaceCapacity, setWorkspaceCapacity] = useState(
    getCurrentActiveCapacity(settings?.timeTracking.defaultCapacityTimeline)
  );
  // const [value, setValue] = useState<string>(
  //   workspaceCapacity === 0 ? "" : convertMsToNatural(workspaceCapacity)
  // );
  const { data: userCapacities } = useGetUserCapacities();
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setWorkspaceCapacity(
        getCurrentActiveCapacity(settings?.timeTracking.defaultCapacityTimeline)
      );
    }
  }, [isEditing]);
  const hours = useMemo(() => {
    return Math.floor(workspaceCapacity / 3600000).toFixed(0);
  }, [workspaceCapacity]);
  const minutes = useMemo(() => {
    return ((workspaceCapacity % 3600000) / 60000).toFixed(0);
  }, [workspaceCapacity]);

  const workspaceHours = useMemo(() => {
    return Math.floor((workspaceCapacity * 5) / 3600000).toFixed(0);
  }, [workspaceCapacity]);
  const workspaceMinutes = useMemo(() => {
    return (((workspaceCapacity * 5) % 3600000) / 60000).toFixed(0);
  }, [workspaceCapacity]);
  const [tab, setTab] = useState<"edit" | "history" | "logs">("edit");
  const [isSaving, setIsSaving] = useState(false);
  const [startingToday, setStartingToday] = useState(true);
  const [newEffectiveDate, setNewEffectiveDate] = useState<Date>(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  useEffect(() => {
    if (updateInstanceCapacity.isSuccess) {
      setIsSaving(false);
      setIsEditing(false);
    }
  }, [updateInstanceCapacity.isSuccess]);

  return (
    <>
      <div>
        <div className="mt-12 border-b border-gray-200/70 pb-10">
          <div className="space-y-3.5">
            <div className="font-medium leading-none">
              Default Weekly Capacity
            </div>
            <div className="max-w-prose text-sm font-light text-gray-600">
              The default weekly capacity used for team members. If a member has
              a custom capacity set, that will be used instead.
            </div>
          </div>
          <div className="mt-4">
            {/* <TextInput
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
              }}
              autoFocus
              placeholder="e.g. 1h 30m"
              className={clsx(
                !parse(value) &&
                  value !== "" &&
                  "border-red-500 ring-[0.5px] ring-red-500"
              )}
              onBlur={(e) => {
                if (value !== "") {
                  const parsed = parse(value);
                  if (parsed === undefined) {
                    return;
                  } else {
                    setWorkspaceCapacity(parsed);
                  }
                }
              }}
            /> */}
            <div className="flex h-9 w-48 items-center justify-between space-x-2 rounded-md border px-2 shadow-sm">
              <div className="text-sm">
                {convertMsToNatural(workspaceCapacityToDisplay * 5)}
              </div>
              <Button
                variant={"ghost"}
                size={"icon"}
                className="h-6 w-6"
                onClick={() => setIsEditing(true)}
              >
                <Pencil2Icon className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-10 pb-12">
          <div className="space-y-3.5">
            <div className="font-medium leading-none">User Capacities</div>
            <div className="max-w-prose text-sm font-light text-gray-600">
              View and edit the capacities for each user.
            </div>
          </div>
          {!!userCapacities ? (
            <div className="mt-6">
              <div className="flex items-center border-b border-gray-200 pb-3 font-normal text-gray-500">
                <div className="flex-1 text-sm leading-none">Name</div>
                <div className="w-28 text-center text-sm leading-none">
                  Custom
                </div>
                {/* <div className="w-40 text-center text-sm leading-none">
                Daily Capacity
              </div> */}
                <div className="w-32 text-center text-sm leading-none">
                  Weekly Capacity
                </div>
                <div className="w-16" />
              </div>
              {userCapacities?.map((userCapacity) => (
                <UserCapacity
                  key={userCapacity.email}
                  userCapacity={userCapacity}
                />
              ))}
            </div>
          ) : (
            <Loading className="mx-auto mt-12" />
          )}
        </div>
      </div>
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="top-[15%] max-h-[70vh] translate-y-[0%] data-[state=closed]:slide-out-to-top-[0%] data-[state=open]:slide-in-from-top-[0%]">
          <DialogHeader>
            <DialogTitle>Edit Default Capacity</DialogTitle>
            <DialogDescription>
              Your weekly capacity is calculated based on a daily capacity.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <Tabs
              defaultValue="home"
              value={tab}
              className="flex flex-1 flex-col overflow-auto pt-1"
              onValueChange={(value) => {
                setTab(value as "edit" | "history");
              }}
            >
              <TabsList className="shrink-0 border-gray-200/70">
                <TabsTrigger className="font-normal" value="edit">
                  Edit
                </TabsTrigger>
                <TabsTrigger className="font-normal" value="history">
                  Active Capacities
                </TabsTrigger>
                <TabsTrigger className="font-normal" value="logs">
                  Changelog
                </TabsTrigger>
              </TabsList>
              <div className="flex-1 overflow-auto">
                <TabsContent value="edit" className="h-full">
                  <div className="space-y-6 pt-6">
                    <div className="space-y-1">
                      <InputHeading heading="Daily Capacity" />
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-1">
                          <Select
                            value={hours}
                            onValueChange={(value) => {
                              const hrs = parseInt(value);
                              const mins = parseInt(minutes);
                              const newTimeInMs = hrs * 3600000 + mins * 60000;
                              setWorkspaceCapacity(newTimeInMs);
                            }}
                          >
                            <SelectTrigger className="h-8 w-16">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1</SelectItem>
                              <SelectItem value="2">2</SelectItem>
                              <SelectItem value="3">3</SelectItem>
                              <SelectItem value="4">4</SelectItem>
                              <SelectItem value="5">5</SelectItem>
                              <SelectItem value="6">6</SelectItem>
                              <SelectItem value="7">7</SelectItem>
                              <SelectItem value="8">8</SelectItem>
                              <SelectItem value="9">9</SelectItem>
                              <SelectItem value="10">10</SelectItem>
                              <SelectItem value="11">11</SelectItem>
                              <SelectItem value="12">12</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="text-sm">hrs</div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Select
                            value={minutes}
                            onValueChange={(value) => {
                              const hrs = parseInt(hours);
                              const mins = parseInt(value);
                              const newTimeInMs = hrs * 3600000 + mins * 60000;
                              setWorkspaceCapacity(newTimeInMs);
                            }}
                          >
                            <SelectTrigger className="h-8 w-16">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0</SelectItem>
                              <SelectItem value="5">5</SelectItem>
                              <SelectItem value="10">10</SelectItem>
                              <SelectItem value="15">15</SelectItem>
                              <SelectItem value="20">20</SelectItem>
                              <SelectItem value="25">25</SelectItem>
                              <SelectItem value="30">30</SelectItem>
                              <SelectItem value="35">35</SelectItem>
                              <SelectItem value="40">40</SelectItem>
                              <SelectItem value="45">45</SelectItem>
                              <SelectItem value="50">50</SelectItem>
                              <SelectItem value="55">55</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="text-sm">mins</div>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <InputHeading heading="Weekly Capacity" />
                      <div className="flex h-8 w-48 cursor-not-allowed items-center space-x-2 rounded-md border px-2.5 shadow-sm">
                        <div className="flex items-center space-x-1">
                          <div className="text-sm">{workspaceHours} hrs</div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="text-sm">{workspaceMinutes} mins</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant={"secondary"}
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={
                          workspaceCapacity ===
                          getCurrentActiveCapacity(
                            settings?.timeTracking.defaultCapacityTimeline
                          )
                        }
                        onClick={() => {
                          setIsSaving(true);
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="history" className="h-full overflow-auto">
                  <InstanceCapacityHistory
                    capacityTimeline={
                      settings?.timeTracking.defaultCapacityTimeline
                    }
                  />
                </TabsContent>
                <TabsContent
                  value="logs"
                  className="flex h-full flex-col overflow-auto"
                >
                  <InstanceCapacityLogs
                    capacities={settings.timeTracking.defaultCapacities}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isSaving} onOpenChange={setIsSaving}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Capacity Change</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            When do you want the new capacity to be in effect from?
          </div>
          <div className="space-y-6 py-4">
            <div className="flex space-x-2.5">
              <div className="pt-[3px]">
                <Checkbox
                  checked={startingToday}
                  onCheckedChange={(checked) => {
                    setStartingToday(!!checked);
                  }}
                />
              </div>
              <div className="space-y-0.5">
                <div className="text-sm">Starting today</div>
                <div className="text-xs font-light text-gray-600">
                  The new capacity will be in effect from today.
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex space-x-2.5">
                <div className="pt-[3px]">
                  <Checkbox
                    checked={!startingToday}
                    onCheckedChange={(checked) => {
                      setStartingToday(!checked);
                    }}
                  />
                </div>
                <div className="space-y-0.5">
                  <div className="text-sm">Backdate</div>
                  <div className="text-xs font-light text-gray-600">
                    The new capacity will be in effect from a previous date
                    onwards.
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"secondary"}
                      className="ml-[26px] w-60 justify-start font-normal"
                      disabled={startingToday}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dayjs(newEffectiveDate).format("MMM DD, YYYY")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-fit p-0" align="center">
                    <Calendar
                      mode="single"
                      selected={newEffectiveDate}
                      defaultMonth={newEffectiveDate}
                      onSelect={(date) => {
                        if (date) {
                          setNewEffectiveDate(date);
                        }
                        setCalendarOpen(false);
                      }}
                      initialFocus
                      disabled={(date) =>
                        date > new Date() || date < new Date("1980-01-01")
                      }
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              variant={"secondary"}
              onClick={() => {
                setIsSaving(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={updateInstanceCapacity.isLoading}
              onClick={() => {
                updateInstanceCapacity.mutate({
                  effectiveFrom: startingToday
                    ? dayjs().format("YYYY-MM-DD")
                    : dayjs(newEffectiveDate).format("YYYY-MM-DD"),
                  defaultCapacity: workspaceCapacity,
                });
              }}
            >
              {updateInstanceCapacity.isLoading ? "Saving.." : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InstanceCapacityLogs({ capacities }: { capacities: CapacityData[] }) {
  return (
    <>
      <div className="flex items-center border-b border-gray-200/70 pb-3 pt-6 font-normal text-gray-500">
        <div className="flex-1 text-sm leading-none">Weekly Capacity</div>
        <div className="w-32 text-center text-sm leading-none">
          Effective From
        </div>
        <div className="w-32 text-center text-sm leading-none">Created On</div>
      </div>
      <div className="flex-1 overflow-auto">
        {capacities.map((capacity, index) => (
          <div
            key={index}
            className="flex h-10 items-center border-b border-gray-100 text-sm last:border-0"
          >
            <div className="flex-1 truncate">
              {convertMsToNatural(capacity.value * 5)}
            </div>
            <div className="w-32 text-center text-sm leading-none">
              {dayjs(capacity.effectiveFrom).format("MMM DD, YYYY")}
            </div>
            <div className="w-32 text-center text-sm leading-none">
              {dayjs(capacity.createdAt).format("MMM DD, YYYY")}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function InstanceCapacityHistory({
  capacityTimeline,
}: {
  capacityTimeline: CapacityTimelineData[];
}) {
  return (
    <div>
      <div className="flex items-center border-b border-gray-200/70 pb-3 pt-6 font-normal text-gray-500">
        <div className="flex-1 text-sm leading-none">Weekly Capacity</div>
        <div className="w-32 text-center text-sm leading-none">Start Date</div>
        <div className="w-32 text-center text-sm leading-none">End Date</div>
      </div>
      {[...capacityTimeline].reverse().map((capacity) => (
        <div
          key={capacity.startDate}
          className="flex h-10 items-center border-b border-gray-100 text-sm last:border-0"
        >
          <div className="flex-1 truncate">
            {convertMsToNatural(capacity.value * 5)}
          </div>
          <div className="w-32 text-center text-sm leading-none">
            {dayjs(capacity.startDate).format("MMM DD, YYYY")}
          </div>
          <div className="w-32 text-center text-sm leading-none">
            {capacity.endDate
              ? dayjs(capacity.endDate).format("MMM DD, YYYY")
              : "-"}
          </div>
        </div>
      ))}
    </div>
  );
}

function UserCapacity({ userCapacity }: { userCapacity: UserAppsData }) {
  const [activeCapacity, setActiveCapacity] = useState(() =>
    userCapacity.timeTracking.capacityTimeline.find(
      (capacity) => !capacity.endDate
    )
  );
  const [userCapacityValue, setUserCapacityValue] = useState(
    getCurrentActiveCapacity(userCapacity.timeTracking.capacityTimeline)
  );
  const updateUserCapacity = useUpdateUserCapacity();

  useEffect(() => {
    if (!!userCapacity) {
      const newActiveCapacity = userCapacity.timeTracking.capacityTimeline.find(
        (capacity) => !capacity.endDate
      );
      setActiveCapacity(newActiveCapacity);
    }
  }, [userCapacity]);

  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tab, setTab] = useState<"edit" | "history" | "logs">("edit");
  const hours = useMemo(() => {
    return Math.floor(userCapacityValue / 3600000).toFixed(0);
  }, [userCapacityValue]);
  const minutes = useMemo(() => {
    return ((userCapacityValue % 3600000) / 60000).toFixed(0);
  }, [userCapacityValue]);

  const weekHours = useMemo(() => {
    return Math.floor((userCapacityValue * 5) / 3600000).toFixed(0);
  }, [userCapacityValue]);
  const weekMinutes = useMemo(() => {
    return (((userCapacityValue * 5) % 3600000) / 60000).toFixed(0);
  }, [userCapacityValue]);
  const [startingToday, setStartingToday] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [newEffectiveDate, setNewEffectiveDate] = useState<Date>(new Date());

  useEffect(() => {
    if (updateUserCapacity.isSuccess) {
      setIsSaving(false);
      setIsEditing(false);
    }
  }, [updateUserCapacity.isSuccess]);

  return (
    <div className="flex h-10 items-center border-b border-gray-100 text-sm last:border-0">
      <div className="flex-1 truncate">{userCapacity.name}</div>
      <div className="flex w-28 items-center justify-center text-center">
        {activeCapacity?.source === "user" ? (
          <CheckIcon className="h-4 w-4" />
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </div>
      {/* <div className="w-40 text-center">
        {convertMsToNatural(currentActiveCapacity)}
      </div> */}
      <div className="w-32 text-center">
        {convertMsToNatural(!!activeCapacity ? activeCapacity.value * 5 : 0)}
      </div>
      <div className="flex w-16 justify-end">
        <DropdownMenu
          open={optionsOpen}
          onOpenChange={(isOpen) => {
            setOptionsOpen(isOpen);
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant={"ghost"}
              className={clsx(
                "h-6 w-6 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
                !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
              )}
            >
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-44" align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem
                // className="py-1 focus:bg-gray-50 focus:text-red-600"
                onClick={(e) => {
                  setIsEditing(true);
                  e.stopPropagation();
                }}
                // className="py-1"
              >
                <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="ml-2 text-sm">Edit</span>
              </DropdownMenuItem>
              {/* <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      className="focus:bg-red-50 focus:text-red-600"
                      onClick={(e) => {
                        setIsDeleting(true);
                        e.stopPropagation();
                      }}
                    >
                      <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Delete</span>
                    </DropdownMenuItem> */}
              {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent className="top-[15%] max-h-[70vh] translate-y-[0%] data-[state=closed]:slide-out-to-top-[0%] data-[state=open]:slide-in-from-top-[0%]">
            <DialogHeader>
              <DialogTitle>Edit User Capacity</DialogTitle>
              <DialogDescription>
                A user's weekly capacity is calculated based on a daily
                capacity.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 pt-4">
              <Tabs
                defaultValue="home"
                value={tab}
                className="flex flex-1 flex-col overflow-auto pt-1"
                onValueChange={(value) => {
                  setTab(value as "edit" | "history");
                }}
              >
                <TabsList className="shrink-0 border-gray-200/70">
                  <TabsTrigger className="font-normal" value="edit">
                    Edit
                  </TabsTrigger>
                  <TabsTrigger className="font-normal" value="history">
                    Active Capacities
                  </TabsTrigger>
                  <TabsTrigger className="font-normal" value="logs">
                    Changelog
                  </TabsTrigger>
                </TabsList>
                <div className="flex-1 overflow-auto">
                  <TabsContent value="edit" className="h-full">
                    <div className="space-y-6 pt-6">
                      <div className="space-y-1">
                        <InputHeading heading="Daily Capacity" />
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <Select
                              value={hours}
                              onValueChange={(value) => {
                                const hrs = parseInt(value);
                                const mins = parseInt(minutes);
                                const newTimeInMs =
                                  hrs * 3600000 + mins * 60000;
                                setUserCapacityValue(newTimeInMs);
                              }}
                            >
                              <SelectTrigger className="h-8 w-16">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">1</SelectItem>
                                <SelectItem value="2">2</SelectItem>
                                <SelectItem value="3">3</SelectItem>
                                <SelectItem value="4">4</SelectItem>
                                <SelectItem value="5">5</SelectItem>
                                <SelectItem value="6">6</SelectItem>
                                <SelectItem value="7">7</SelectItem>
                                <SelectItem value="8">8</SelectItem>
                                <SelectItem value="9">9</SelectItem>
                                <SelectItem value="10">10</SelectItem>
                                <SelectItem value="11">11</SelectItem>
                                <SelectItem value="12">12</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="text-sm">hrs</div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Select
                              value={minutes}
                              onValueChange={(value) => {
                                const hrs = parseInt(hours);
                                const mins = parseInt(value);
                                const newTimeInMs =
                                  hrs * 3600000 + mins * 60000;
                                setUserCapacityValue(newTimeInMs);
                              }}
                            >
                              <SelectTrigger className="h-8 w-16">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">0</SelectItem>
                                <SelectItem value="5">5</SelectItem>
                                <SelectItem value="10">10</SelectItem>
                                <SelectItem value="15">15</SelectItem>
                                <SelectItem value="20">20</SelectItem>
                                <SelectItem value="25">25</SelectItem>
                                <SelectItem value="30">30</SelectItem>
                                <SelectItem value="35">35</SelectItem>
                                <SelectItem value="40">40</SelectItem>
                                <SelectItem value="45">45</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                                <SelectItem value="55">55</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="text-sm">mins</div>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <InputHeading heading="Weekly Capacity" />
                        <div className="flex h-8 w-48 cursor-not-allowed items-center space-x-2 rounded-md border px-2.5 shadow-sm">
                          <div className="flex items-center space-x-1">
                            <div className="text-sm">{weekHours} hrs</div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div className="text-sm">{weekMinutes} mins</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant={"secondary"}
                          onClick={() => setIsEditing(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          disabled={
                            userCapacityValue ===
                            getCurrentActiveCapacity(
                              userCapacity.timeTracking.capacityTimeline
                            )
                          }
                          onClick={() => {
                            setIsSaving(true);
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="history" className="h-full overflow-auto">
                    <UserCapacityHistory
                      capacityTimeline={
                        userCapacity.timeTracking.capacityTimeline
                      }
                    />
                  </TabsContent>
                  <TabsContent
                    value="logs"
                    className="flex h-full flex-col overflow-auto"
                  >
                    <UserCapacityLogs
                      capacities={userCapacity.timeTracking.capacities}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={isSaving} onOpenChange={setIsSaving}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Capacity Change</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              When do you want the new capacity to be in effect from?
            </div>
            <div className="space-y-6 py-4">
              <div className="flex space-x-2.5">
                <div className="pt-[3px]">
                  <Checkbox
                    checked={startingToday}
                    onCheckedChange={(checked) => {
                      setStartingToday(!!checked);
                    }}
                  />
                </div>
                <div className="space-y-0.5">
                  <div className="text-sm">Starting today</div>
                  <div className="text-xs font-light text-gray-600">
                    The new capacity will be in effect from today.
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex space-x-2.5">
                  <div className="pt-[3px]">
                    <Checkbox
                      checked={!startingToday}
                      onCheckedChange={(checked) => {
                        setStartingToday(!checked);
                      }}
                    />
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-sm">Backdate</div>
                    <div className="text-xs font-light text-gray-600">
                      The new capacity will be in effect from a previous date
                      onwards.
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"secondary"}
                        className="ml-[26px] w-60 justify-start font-normal"
                        disabled={startingToday}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dayjs(newEffectiveDate).format("MMM DD, YYYY")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-fit p-0" align="center">
                      <Calendar
                        mode="single"
                        selected={newEffectiveDate}
                        defaultMonth={newEffectiveDate}
                        onSelect={(date) => {
                          if (date) {
                            setNewEffectiveDate(date);
                          }
                          setCalendarOpen(false);
                        }}
                        initialFocus
                        disabled={(date) =>
                          date > new Date() || date < new Date("1980-01-01")
                        }
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant={"secondary"}
                onClick={() => {
                  setIsSaving(false);
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={updateUserCapacity.isLoading}
                onClick={() => {
                  updateUserCapacity.mutate({
                    effectiveFrom: startingToday
                      ? dayjs().format("YYYY-MM-DD")
                      : dayjs(newEffectiveDate).format("YYYY-MM-DD"),
                    capacity: userCapacityValue,
                    userId: userCapacity._id,
                  });
                }}
              >
                {updateUserCapacity.isLoading ? "Saving.." : "Save"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function UserCapacityLogs({ capacities }: { capacities: CapacityData[] }) {
  return (
    <>
      <div className="flex items-center border-b border-gray-200/70 pb-3 pt-6 font-normal text-gray-500">
        <div className="flex-1 text-sm leading-none">Weekly Capacity</div>
        <div className="w-32 text-center text-sm leading-none">
          Effective From
        </div>
        <div className="w-32 text-center text-sm leading-none">Created On</div>
      </div>
      <div className="flex-1 overflow-auto">
        {capacities.map((capacity, index) => (
          <div
            key={index}
            className="flex h-10 items-center border-b border-gray-100 text-sm last:border-0"
          >
            <div className="flex-1 truncate">
              {convertMsToNatural(capacity.value * 5)}
            </div>
            <div className="w-32 text-center text-sm leading-none">
              {dayjs(capacity.effectiveFrom).format("MMM DD, YYYY")}
            </div>
            <div className="w-32 text-center text-sm leading-none">
              {dayjs(capacity.createdAt).format("MMM DD, YYYY")}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function UserCapacityHistory({
  capacityTimeline,
}: {
  capacityTimeline: CapacityTimelineData[];
}) {
  return (
    <div>
      <div className="flex items-center border-b border-gray-200/70 pb-3 pt-6 font-normal text-gray-500">
        <div className="flex-1 text-sm leading-none">Weekly Capacity</div>
        <div className="w-32 text-center text-sm leading-none">Start Date</div>
        <div className="w-32 text-center text-sm leading-none">End Date</div>
      </div>
      {[...capacityTimeline].reverse().map((capacity) => (
        <div
          key={capacity.startDate}
          className="flex h-10 items-center border-b border-gray-100 text-sm last:border-0"
        >
          <div className="flex-1 truncate">
            {convertMsToNatural(capacity.value * 5)}
          </div>
          <div className="w-32 text-center text-sm leading-none">
            {dayjs(capacity.startDate).format("MMM DD, YYYY")}
          </div>
          <div className="w-32 text-center text-sm leading-none">
            {capacity.endDate
              ? dayjs(capacity.endDate).format("MMM DD, YYYY")
              : "-"}
          </div>
        </div>
      ))}
    </div>
  );
}

function getCurrentActiveCapacity(
  userCapacities: CapacityTimelineData[]
): number {
  const currentOne = userCapacities.find((capacity) => !capacity.endDate);
  // console.log(currentOne?.value);
  return currentOne?.value ?? 0;
}
